import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
// Paleta institucional Elecom — tokens compartidos (ver src/theme/colors.js)
import { COLORS as C } from '../theme/colors';

/**
 * @description Página de inicio de sesión.
 * Lógica sin cambios — solo rediseño visual con paleta institucional.
 */
const Login = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const auth     = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await auth.login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Credenciales incorrectas. Verifique su email y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Logo */}
        <img src="/logo.png" alt="Elecom Elevator Components" style={styles.logo} />

        {/* Separador */}
        <div style={styles.divider} />

        {/* Título */}
        <h1 style={styles.title}>Bienvenido</h1>
        <p style={styles.subtitle}>Ingrese sus credenciales para continuar</p>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={styles.form}>

          {/* Email */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="email">Correo electrónico</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>✉</span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                required
                autoComplete="email"
                style={styles.input}
                onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                onBlur={(e)  => Object.assign(e.target.style, styles.input)}
              />
            </div>
          </div>

          {/* Contraseña */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="password">Contraseña</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>🔒</span>
              <input
                id="password"
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                style={{ ...styles.input, paddingRight: '40px' }}
                onFocus={(e) => Object.assign(e.target.style, { ...styles.inputFocus, paddingRight: '40px' })}
                onBlur={(e)  => Object.assign(e.target.style, { ...styles.input,      paddingRight: '40px' })}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                style={styles.toggleBtn}
                tabIndex={-1}
                aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={styles.errorBox} role="alert">
              {error}
            </div>
          )}

          {/* Botón */}
          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading
              ? <span style={styles.spinner} />
              : 'Ingresar'
            }
          </button>
        </form>

        {/* Pie */}
        <p style={styles.footer}>Elecom Elevator Components</p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.pageBg,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: C.white,
    borderRadius: '14px',
    padding: '40px 36px 32px',
    boxShadow: '0 4px 24px rgba(56,62,65,0.1)',
    border: '1px solid rgba(56,62,65,0.08)',
    boxSizing: 'border-box',
  },
  logo: {
    display: 'block',
    margin: '0 auto 16px',
    height: '52px',
    objectFit: 'contain',
  },
  divider: {
    width: '40px',
    height: '2px',
    backgroundColor: 'rgba(56,62,65,0.15)',
    borderRadius: '1px',
    margin: '0 auto 24px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: C.teal,
    textAlign: 'center',
    margin: '0 0 6px',
    letterSpacing: '0.3px',
  },
  subtitle: {
    fontSize: '13px',
    color: 'rgba(56,62,65,0.5)',
    textAlign: 'center',
    margin: '0 0 28px',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'rgba(56,62,65,0.6)',
    letterSpacing: '0.3px',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    fontSize: '14px',
    pointerEvents: 'none',
    opacity: 0.5,
  },
  input: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: `1px solid rgba(56,62,65,0.2)`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  inputFocus: {
    width: '100%',
    padding: '10px 12px 10px 36px',
    fontSize: '14px',
    color: C.charcoal,
    backgroundColor: C.white,
    border: `1.5px solid ${C.teal}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  toggleBtn: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px',
    opacity: 0.55,
    lineHeight: 1,
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  submitBtn: {
    width: '100%',
    height: '46px',
    backgroundColor: C.teal,
    color: C.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
    opacity: 1,
    transition: 'opacity 0.15s',
  },
  spinner: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    border: '2.5px solid rgba(255,255,255,0.35)',
    borderTop: '2.5px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  registerLink: {
    textAlign: 'center',
    fontSize: '13px',
    color: 'rgba(56,62,65,0.55)',
    marginTop: '20px',
    marginBottom: '0',
  },
  link: {
    color: C.teal,
    fontWeight: '600',
    textDecoration: 'none',
  },
  footer: {
    textAlign: 'center',
    fontSize: '11px',
    color: 'rgba(56,62,65,0.3)',
    letterSpacing: '0.8px',
    marginTop: '12px',
    marginBottom: '0',
  },
};

export default Login;
